import { useEffect, useMemo } from 'react';

import { FeatureFlag } from '../enums/FeatureFlag';
import { buildContextWithKey } from '../functions/buildContextWithKey';
import useFeatureStore from '../useFeatureStore';

/**
 * Returns the flag value from cached state and asks the store to fetch latest value
 * and handle metrics outside of component rendering.
 */
export const useFeatureFlag = (
  flag: FeatureFlag,
  context?: Record<string, unknown>,
): boolean => {
  const defaultContext = useFeatureStore((state): Record<string, unknown> => state.defaultContext);

  // Parents may pass a new context object every render (new reference, same contents).
  // Stringify lets us compare contents, so effects run only when the contents change.
  const contextJson = useMemo((): string => JSON.stringify(context ?? {}), [context]);
  const parsedContext = useMemo((): any => JSON.parse(contextJson), [contextJson]);

  const contextKey = useMemo((): string => {
    const [, key] = buildContextWithKey(defaultContext, parsedContext);
    return key;
  }, [defaultContext, parsedContext]);

  const enabled = useFeatureStore((state): boolean =>
    state.getCachedEnabled(flag, parsedContext),
  );

  // Trigger evaluation (fetch + metrics) outside render
  useEffect((): void => {
    useFeatureStore.getState().isEnabled(flag, parsedContext);
  }, [flag, contextKey]);

  return enabled;
};
